import { useMemo } from 'react';
import { useSessions } from './useSessions';
import { useRidersById } from './useRiders';
import { compareSessionsAsc, filterNavigableSessions } from '../utils/sessionSort';

export interface SessionNavigationIds {
  previousSessionId: string | null;
  nextSessionId: string | null;
}

// Next/prev neighbours of a session in date order, restricted to navigable
// sessions (normal, non-hidden sessions + the freeride singleton — see
// filterNavigableSessions). The session being viewed is always kept, so the
// arrows still work when a hidden session is opened from the list.
// Pass enabled=false to skip (e.g. new sessions).
export function useSessionNavigation(sessionId: string, enabled: boolean = true): SessionNavigationIds {
  const { data: allSessions } = useSessions();
  const { data: ridersById } = useRidersById();

  return useMemo(() => {
    if (!allSessions || !sessionId || !enabled) {
      return { previousSessionId: null, nextSessionId: null };
    }

    // No defensive copy needed: filterNavigableSessions returns a fresh array,
    // so the sort below cannot mutate the query cache.
    const sortedSessions = filterNavigableSessions(allSessions, sessionId)
      .sort((a, b) => compareSessionsAsc(a, b, ridersById));

    const currentIndex = sortedSessions.findIndex((s) => s.id === sessionId);
    if (currentIndex === -1) {
      return { previousSessionId: null, nextSessionId: null };
    }

    return {
      previousSessionId: currentIndex > 0 ? sortedSessions[currentIndex - 1].id : null,
      nextSessionId: currentIndex < sortedSessions.length - 1 ? sortedSessions[currentIndex + 1].id : null,
    };
  }, [allSessions, sessionId, enabled, ridersById]);
}
